'use client';

import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

// The directory used to filter server-side off `searchParams.filter`, which made
// /projects a dynamic route: rendered on every request, never cached, ~0.5-1s TTFB
// on the target of the "Apps" nav item. The filter is a view concern over a handful
// of apps, so it lives on the client now and the page stays prerendered.
// `window.location.search` rather than `useSearchParams`, which would force the same
// dynamic bailout unless this were wrapped in Suspense.
//
// Layout note: the apps come first, directly under the hero. This page used to open
// with two full titled sections of navigation chrome — six "route" cards and then a
// filter row, each with its own eyebrow, h2 and paragraph — so a reader scrolled past
// roughly a screen and a half of signposting before seeing a single app. Both controls
// did the same thing (set one filter), and `projects` in lib/site.ts is narrowed to the
// two Cleanor listings, so most of that navigation pointed at nothing. The cards are
// gone and the filter is a slim toolbar that hides itself when it cannot narrow.

export type DirectoryApp = {
  slug: string;
  name: string;
  summary: string;
  platform: string;
  operatingSystem: string;
  stage: 'Live' | 'In review' | 'Coming soon';
  href: string;
  iconSrc?: string;
};

type ProjectDirectoryFilter =
  | 'all'
  | 'iphone'
  | 'android'
  | 'live'
  | 'almost-out'
  | 'storage'
  | 'documents'
  | 'media'
  | 'ai';

const projectFilterMeta: Record<ProjectDirectoryFilter, { label: string; description: string }> = {
  all: {
    label: 'All apps',
    description:
      'Browse the full lineup across live releases, previews, and the next product tracks.',
  },
  iphone: {
    label: 'iPhone',
    description: 'Open the iPhone cleanup path and the apps already tuned for iOS.',
  },
  android: {
    label: 'Android',
    description: 'Browse the Android utilities that are already live or close to release.',
  },
  live: {
    label: 'Live now',
    description: 'See the apps that already have an active App Store or Google Play path.',
  },
  'almost-out': {
    label: 'Almost out',
    description: 'Check the apps that are built, in review, and close to public release.',
  },
  storage: {
    label: 'Phone storage',
    description:
      'Open the storage-cleanup apps for duplicate photos, screenshots, videos, and device clutter.',
  },
  documents: {
    label: 'Documents and PDF',
    description: 'Go straight to the scan, convert, and PDF utility line.',
  },
  media: {
    label: 'Photo and video',
    description: 'Browse the apps built around lighter image and video exports.',
  },
  ai: {
    label: 'AI experiments',
    description: 'See the next creative AI app track that is still being prepared.',
  },
};

const directorySections = [
  {
    stage: 'Live' as const,
    eyebrow: 'Available now',
    title: 'Ready to install',
    description: 'Live apps already shipping across the App Store and Google Play.',
  },
  {
    stage: 'In review' as const,
    eyebrow: 'In review',
    title: 'Almost out',
    description: 'Apps already built and waiting on store approval or rollout.',
  },
  {
    stage: 'Coming soon' as const,
    eyebrow: 'Coming next',
    title: 'Still in the lab',
    description: 'The next product tracks that are not install-ready yet.',
  },
];

const placeholderThemes = [
  ['#4d72ff', '#7aa8ff'],
  ['#4a56e2', '#7a88ff'],
  ['#149a71', '#74d9aa'],
  ['#f08c36', '#ffd37a'],
  ['#7956f4', '#ab92ff'],
] as const;

// Icons are the largest paint candidate now that the list starts at the top of the
// page, so the ones that land above the fold must not be lazy — a lazy LCP image is
// a guaranteed LCP penalty. Everything past the first screenful still defers.
const EAGER_ICON_COUNT = 4;

function matchesProjectFilter(app: DirectoryApp, filter: ProjectDirectoryFilter) {
  switch (filter) {
    case 'iphone':
      return app.operatingSystem.includes('iOS');
    case 'android':
      return app.operatingSystem.includes('Android');
    case 'live':
      return app.stage === 'Live';
    case 'almost-out':
      return app.stage === 'In review';
    case 'storage':
      return ['clenoir', 'cleanor'].includes(app.slug);
    case 'documents':
      return ['image-to-pdf-converter', 'pdf-scanner-editor'].includes(app.slug);
    case 'media':
      return ['photo-compressor-resizer', 'video-compressor-resizer'].includes(app.slug);
    case 'ai':
      return app.slug === 'another-you';
    case 'all':
    default:
      return true;
  }
}

function normalizeProjectFilter(value: string | null): ProjectDirectoryFilter {
  if (!value) {
    return 'all';
  }

  return value in projectFilterMeta ? (value as ProjectDirectoryFilter) : 'all';
}

function getProjectFilterHref(filter: ProjectDirectoryFilter) {
  return filter === 'all' ? '/projects' : `/projects?filter=${filter}`;
}

function getProjectInitials(name: string) {
  const words = name
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  const initials = words.slice(0, 2).map((word) => word[0]?.toUpperCase() ?? '');
  return initials.join('') || 'CL';
}

function getPlaceholderStyle(slug: string) {
  const sum = Array.from(slug).reduce((total, char) => total + char.charCodeAt(0), 0);
  const [start, end] = placeholderThemes[sum % placeholderThemes.length];

  return {
    '--app-placeholder-start': start,
    '--app-placeholder-end': end,
  } as CSSProperties;
}

function DirectoryIcon({ app, eager }: { app: DirectoryApp; eager: boolean }) {
  if (app.iconSrc) {
    return (
      <img
        className="app-directory-icon-image"
        src={app.iconSrc}
        alt={`${app.name} app icon`}
        width={88}
        height={88}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
        decoding="async"
      />
    );
  }

  return (
    <div className="app-directory-icon-placeholder" style={getPlaceholderStyle(app.slug)}>
      <strong>{getProjectInitials(app.name)}</strong>
      <span>{app.platform}</span>
    </div>
  );
}

// `h3`: the app name sits under its section's h2. It used to be an h2 itself, which
// put eight app names on the same outline level as the three section titles and left
// the page with a flat wall of h2s.
function DirectoryRow({ app, eager }: { app: DirectoryApp; eager: boolean }) {
  return (
    <Link className="app-directory-item" href={app.href}>
      <div className="app-directory-icon">
        <DirectoryIcon app={app} eager={eager} />
      </div>

      <div className="app-directory-copy">
        <h3>{app.name}</h3>
        <p className="app-directory-summary">{app.summary}</p>
      </div>
    </Link>
  );
}

export default function AppsDirectory({ apps }: { apps: DirectoryApp[] }) {
  // Starts at 'all' so the server render and the first client render agree; a shared
  // ?filter= link is applied on mount.
  const [activeFilter, setActiveFilter] = useState<ProjectDirectoryFilter>('all');

  useEffect(() => {
    setActiveFilter(
      normalizeProjectFilter(new URLSearchParams(window.location.search).get('filter')),
    );
  }, []);

  const selectFilter = useCallback((filter: ProjectDirectoryFilter) => {
    setActiveFilter(filter);
    // Keep the address bar shareable without a navigation, which would re-request the
    // page and defeat the point of prerendering it.
    window.history.replaceState(null, '', getProjectFilterHref(filter));
  }, []);

  const filterCounts = Object.fromEntries(
    (Object.keys(projectFilterMeta) as ProjectDirectoryFilter[]).map((filter) => [
      filter,
      apps.filter((app) => matchesProjectFilter(app, filter)).length,
    ]),
  ) as Record<ProjectDirectoryFilter, number>;

  // Two things make a filter chip worthless, and this catalogue hits both.
  //
  // Empty: `projects` is deliberately narrowed to the two Cleanor listings, so
  // documents / media / ai / almost-out match nothing. A "0" chip leads to an empty
  // result and is a dead end.
  //
  // Undiscriminating: with two apps, "Live now" and "Phone storage" each match both of
  // them, i.e. exactly what "All apps" already shows. Three chips, one result set.
  //
  // Platform is the exception. Android matches both apps (the iOS listing declares
  // "iOS, Android"), so the undiscriminating rule would hide it — and a lineup with a
  // dedicated Android app would offer a reader looking for Android nothing to click.
  // Platform is an identity signal, not just a narrowing device, so it stays whenever
  // it has anything behind it.
  const alwaysShow: ProjectDirectoryFilter[] = ['all', 'iphone', 'android'];
  const visibleFilters = (Object.keys(projectFilterMeta) as ProjectDirectoryFilter[]).filter(
    (filter) =>
      // The active filter always renders, so a shared ?filter= link never looks broken.
      filter === activeFilter ||
      (filterCounts[filter] > 0 &&
        (alwaysShow.includes(filter) || filterCounts[filter] < apps.length)),
  );
  // One lonely "All apps" chip is a control that controls nothing.
  const showFilters = visibleFilters.length > 1;
  const filteredApps = apps.filter((app) => matchesProjectFilter(app, activeFilter));
  const activeFilterMeta = projectFilterMeta[activeFilter];
  const filteredSummary =
    filteredApps.length === 1
      ? '1 app matches this view.'
      : `${filteredApps.length} apps match this view.`;

  return (
    <div className="apps-directory-stack">
      {/* A control, not a section: no eyebrow, no h2, no paragraph. Rendered before
          the list because a filter that appears after what it filters is useless, but
          it is one wrapping row, so the first apps stay on the opening screen. */}
      {showFilters ? (
        <div
          className="directory-filter-row"
          role="group"
          aria-label="Filter apps by platform, release stage, or app line"
        >
          {visibleFilters.map((filter) => (
            <a
              className={`directory-filter-link ${
                activeFilter === filter ? 'directory-filter-link-active' : ''
              }`}
              href={getProjectFilterHref(filter)}
              key={filter}
              aria-current={activeFilter === filter ? 'true' : undefined}
              onClick={(event) => {
                event.preventDefault();
                selectFilter(filter);
              }}
            >
              {projectFilterMeta[filter].label}
              <span>{filterCounts[filter]}</span>
            </a>
          ))}
        </div>
      ) : null}

      {activeFilter !== 'all' ? (
        <section className="overview-section apps-directory-section">
          <div className="section-heading apps-directory-heading">
            <span className="eyebrow">Filtered view</span>
            <h2>{activeFilterMeta.label}</h2>
            <p>{`${activeFilterMeta.description} ${filteredSummary}`}</p>
          </div>

          {filteredApps.length ? (
            <div className="app-directory-list app-project-store-frame">
              {filteredApps.map((app, index) => (
                <DirectoryRow app={app} eager={index < EAGER_ICON_COUNT} key={app.slug} />
              ))}
            </div>
          ) : (
            <div className="directory-filter-empty">
              <p>No apps match this filter yet.</p>
              {/* Link rather than a bare <a>: this points at a real page, and
                  next/lint rejects <a> for internal routes. The click is still
                  intercepted so resetting the filter does not cost a navigation. */}
              <Link
                className="soft-link"
                href="/projects"
                onClick={(event) => {
                  event.preventDefault();
                  selectFilter('all');
                }}
              >
                Show all apps
              </Link>
            </div>
          )}
        </section>
      ) : null}

      {activeFilter === 'all'
        ? (() => {
            // A single running index across the sections, so "first four icons" means
            // the first four on the page rather than the first four of every section.
            let iconIndex = 0;

            return directorySections.map((section) => {
              const sectionApps = apps.filter((app) => app.stage === section.stage);

              if (!sectionApps.length) {
                return null;
              }

              return (
                <section className="overview-section apps-directory-section" key={section.stage}>
                  <div className="section-heading apps-directory-heading">
                    <span className="eyebrow">{section.eyebrow}</span>
                    <h2>{section.title}</h2>
                    <p>{section.description}</p>
                  </div>

                  <div className="app-directory-list app-project-store-frame">
                    {sectionApps.map((app) => {
                      const eager = iconIndex < EAGER_ICON_COUNT;
                      iconIndex += 1;

                      return <DirectoryRow app={app} eager={eager} key={app.slug} />;
                    })}
                  </div>
                </section>
              );
            });
          })()
        : null}
    </div>
  );
}
